import type * as React from "react";
import { useTranslation } from "react-i18next";
import { Lockup } from "../brand/Logo";
import { Footer } from "./Footer";
import { ThemeToggle } from "./ThemeToggle";

/**
 * Candidate-facing shell: brand header (lockup left, slot for status/menus right), centered content, HR footer.
 * Header slots hold optional page context and account controls; timers belong in the page body.
 */
export function AppShell({
  children,
  headerCenter,
  headerRight,
  width = "narrow",
  showHrFooter = true,
}: {
  children: React.ReactNode;
  headerCenter?: React.ReactNode;
  headerRight?: React.ReactNode;
  width?: "narrow" | "wide" | "workbench";
  showHrFooter?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className={`flex min-h-dvh flex-col ${width === "workbench" ? "md:h-dvh md:min-h-0" : ""}`}>
      <a
        href="#main-content"
        className="sr-only-focusable fixed left-4 top-4 z-50 bg-surface px-4 py-3 text-link"
      >
        {t("actions.skipToContent")}
      </a>
      <header className="sticky top-0 z-30 shrink-0 border-b border-line bg-surface">
        <div
          className={`mx-auto flex h-18 w-full items-center gap-4 ${width === "workbench" ? "px-5 sm:px-8" : "max-w-5xl px-4 sm:px-6"}`}
        >
          <a
            href="/"
            className="flex shrink-0 items-center leading-none no-underline hover:no-underline"
            aria-label="SHEN NONG SHI"
          >
            <span className="hidden items-center sm:inline-flex">
              <Lockup />
            </span>
            <span className="inline-flex items-center sm:hidden">
              <Lockup compact />
            </span>
          </a>
          <div className="min-w-0 flex-1">{headerCenter}</div>
          <div className="flex items-center gap-1">
            {headerRight}
            <ThemeToggle />
          </div>
        </div>
      </header>
      <main
        id="main-content"
        tabIndex={-1}
        className={
          width === "narrow"
            ? "mx-auto w-full max-w-3xl flex-1 px-5 py-10 max-[359px]:px-2 sm:px-8 sm:py-14"
            : width === "workbench"
              ? "flex w-full min-h-0 flex-1 flex-col px-5 py-4 sm:px-8"
              : "mx-auto w-full max-w-5xl flex-1 px-5 py-10 sm:px-8 sm:py-14"
        }
      >
        {children}
      </main>
      <Footer showHr={showHrFooter} compact={width === "workbench"} />
    </div>
  );
}
